import { Box, Flex, Text, Button, Select } from "@radix-ui/themes";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { RadixColor } from "@/types/breeze/Theme";
import { TenantRadius, TenantThemeSettings } from "@/types/breeze/Tenant";

const radiusOptions: { value: TenantRadius; label: string; description: string }[] = [
  { value: "none", label: "Square", description: "Sharp corners, no rounding (default)" },
  { value: "medium", label: "Curved", description: "Subtly rounded corners" },
  { value: "full", label: "Round", description: "Fully pill-shaped buttons" },
];

const availableColors: RadixColor[] = [
  "gray",
  "gold",
  "bronze",
  "brown",
  "yellow",
  "amber",
  "orange",
  "tomato",
  "red",
  "ruby",
  "crimson",
  "pink",
  "plum",
  "purple",
  "violet",
  "iris",
  "indigo",
  "blue",
  "cyan",
  "teal",
  "jade",
  "green",
  "grass",
  "lime",
  "mint",
];

const availableGrayColors: (
  | "gray"
  | "mauve"
  | "slate"
  | "sage"
  | "olive"
  | "sand"
)[] = ["gray", "mauve", "slate", "sage", "olive", "sand"];

interface ThemeSettingsProps {
  value: TenantThemeSettings | undefined;
  onChange: (themeSettings: TenantThemeSettings) => void;
}

export function ThemeSettings({ value, onChange }: ThemeSettingsProps) {
  const { updateTenantPrimaryColor, updateTenantGrayColor, updateTenantRadius } =
    useTenantTheme();

  const currentPrimary = value?.primary_color || "orange";
  const currentGray = value?.gray_color || "gray";
  const currentRadius: TenantRadius = value?.radius || "none";

  const handlePrimaryColorChange = (color: RadixColor) => {
    const newThemeSettings = {
      ...value,
      primary_color: color,
    };
    onChange(newThemeSettings);
    updateTenantPrimaryColor(color); // Update theme immediately for preview
  };

  const handleGrayColorChange = (
    color: "gray" | "mauve" | "slate" | "sage" | "olive" | "sand",
  ) => {
    const newThemeSettings = {
      ...value,
      gray_color: color,
    };
    onChange(newThemeSettings);
    updateTenantGrayColor(color); // Update theme immediately for preview
  };

  const handleRadiusChange = (radius: TenantRadius) => {
    const newThemeSettings = {
      ...value,
      radius,
    };
    onChange(newThemeSettings);
    updateTenantRadius(radius); // Update theme immediately for preview
  };

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Text size="2" weight="medium" mb="2" asChild>
          <label>Primary Color</label>
        </Text>
        <Text size="1" color="gray" mb="3" asChild>
          <p>
            This color will be used for buttons, links, and other primary
            interface elements.
          </p>
        </Text>
        <Flex gap="2" wrap="wrap">
          {availableColors.map((color) => (
            <Button
              key={color}
              size="1"
              variant={color === currentPrimary ? "solid" : "soft"}
              color={color}
              onClick={() => handlePrimaryColorChange(color)}
              style={{
                textTransform: "capitalize",
                minWidth: "60px",
              }}
              type="button" // Prevent form submission
            >
              {color}
            </Button>
          ))}
        </Flex>
      </Box>

      <Box>
        <Text size="2" weight="medium" mb="2" asChild>
          <label>Gray Color</label>
        </Text>
        <Text size="1" color="gray" mb="3" asChild>
          <p>This affects the neutral colors used throughout the interface.</p>
        </Text>
        <Select.Root value={currentGray} onValueChange={handleGrayColorChange}>
          <Select.Trigger
            placeholder="Select gray color"
            style={{ width: "200px" }}
          />
          <Select.Content>
            {availableGrayColors.map((color) => (
              <Select.Item key={color} value={color}>
                {color.charAt(0).toUpperCase() + color.slice(1)}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Box>

      <Box>
        <Text size="2" weight="medium" mb="2" asChild>
          <label>Corner Radius</label>
        </Text>
        <Text size="1" color="gray" mb="3" asChild>
          <p>
            Controls the roundness of buttons, inputs, and other interface
            elements across the platform.
          </p>
        </Text>
        <Flex gap="2" wrap="wrap">
          {radiusOptions.map((option) => (
            <Button
              key={option.value}
              size="2"
              variant={option.value === currentRadius ? "solid" : "soft"}
              onClick={() => handleRadiusChange(option.value)}
              title={option.description}
              type="button"
              style={{
                borderRadius:
                  option.value === "none"
                    ? "0"
                    : option.value === "full"
                    ? "9999px"
                    : "8px",
                minWidth: "80px",
              }}
            >
              {option.label}
            </Button>
          ))}
        </Flex>
      </Box>

      <Box>
        <Text size="1" color="gray">
          Theme changes are previewed immediately and will be saved when you
          submit the form.
        </Text>
      </Box>
    </Flex>
  );
}
